import React, { useEffect, useMemo, useState } from "react";
import { StyleSheet, View } from "react-native";
import CustomText from "../components/common/general/CustomText/CustomText";
import { MotiView } from "moti";
import CustomSpacing from "../components/common/layout/CustomSpacing/CustomSpacing";
import {
  GetAssessmentSubmissionsByFacultyDocument,
  GetAssessmentSubmissionsDetailsDocument,
  GetAssessmentSubmissionAttemptHistoryDocument,
  GetGradingCenterDashboardDataDocument,
  GetUserSubmissionsByFacultyDocument,
  useGetAssessmentSubmissionsByFacultyQuery,
  useGetAssessmentSubmissionsDetailsQuery,
  useGetCoursesByFacultyQuery,
  useGetGradingCenterDashboardDataQuery,
  useGetUserSubmissionsByFacultyQuery,
  useMeQuery,
  useUpdateAssessmentSubmissionGradeMutation,
  useUpdateStudentAssignmentGradeMutation,
} from "../graphql/generated/graphql";
import DashboardDataContainer from "../containers/customCoursework/TeacherGradingUIComponents/DashboardDataContainer";
import GradingTableContainer from "../containers/customCoursework/TeacherGradingUIComponents/GradingsTableContainer";
import CustomMultiSelect from "../components/common/general/CustomMultiSelect/CustomMultiSelect";
import AssessmentGradingModalContainer from "../containers/customCoursework/TeacherGradingUIComponents/AssessmentGradingModalContainer";
import AssignmentSubmissionGradingModal from "../containers/customCoursework/TeacherGradingUIComponents/AssignmentSubmissionGradingModalContainer";
import usePagination from "../hooks/usePagination";
import CustomSearchInput from "../components/common/general/CustomSearchInput/CustomSearchInput";
import SortingDropdown from "../components/common/TableControls/SortingDropdown/SortingDropdown";
import useSortingDropdown, { SORT_ITEMS } from "../hooks/useSortingDropdown";
import DefaultLayout from "../layouts/DefaultLayout.web";

enum ModalState {
  CLOSED = "CLOSED",
  VIEW_ASSESSMENT_RESPONSE = "VIEW_ASSESSMENT_RESPONSE",
  VIEW_ASSIGNMENT_RESPONSE = "VIEW_ASSIGNMENT_RESPONSE",
  EDIT_SCORE = "EDIT_SCORE",
}

export interface GradingData {
  userId?: string;
  userName?: string;
  status?: "RUNNING" | "COMPLETED";
  completedAt?: string;
  startedAt?: string;
  totalPoints?: number;
  maximumPoints?: number;
  hasTeacherGraded?: boolean;
  assessmentId?: string;
  created?: string;
  lastModified?: string;
  moduleName?: string;
  courseName?: string;
  type?: string;
  isEnabled?: boolean;
  id?: string;
  questions?: {
    id: string;
    question: string;
    totalScore: number;
    rubrics: {
      id: string;
      component: string;
      points: number;
      pointsAwarded: number;
      maxPoints: number;
      remarks: string;
    }[];
    userAnswer: {
      answer: string;
      createdAt: string;
      totalPoints: number;
      maximumPoints: number;
    };
  }[];
}

const ITEMS_PER_PAGE = 3;

const GradingCenterView = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [modalState, setModalState] = React.useState<ModalState>(ModalState.CLOSED);
  const [selectedGradingData, setSelectedGradingData] = React.useState<GradingData | null>(null);
  const [selectedCourses, setSelectedCourses] = React.useState<string[]>([]);
  const [selectedModules, setSelectedModules] = React.useState<string[]>([]);
  const [updateError, setUpdateError] = React.useState<string | null>(null);

  const { sortBy, setSortBy, sortDropdownOpen, setSortDropdownOpen } = useSortingDropdown({
    defaultSortBy: "date_desc",
  });

  const { data: meData } = useMeQuery();

  const {
    page: assessmentsPage,
    setPage: setAssessmentsPage,
    pageSize: assessmentsPageSize,
    setPageSize: setAssessmentsPageSize,
  } = usePagination({
    itemsPerPage: ITEMS_PER_PAGE,
  });

  const {
    page: submissionsPage,
    setPage: setSubmissionsPage,
    pageSize: submissionsPageSize,
    setPageSize: setSubmissionsPageSize,
  } = usePagination({
    itemsPerPage: ITEMS_PER_PAGE,
  });

  const [updateAssessmentGrade] = useUpdateAssessmentSubmissionGradeMutation({
    refetchQueries: [
      {
        query: GetAssessmentSubmissionsByFacultyDocument,
        variables: {
          input: {
            facultyId: meData?.me?.id || "",
            query: searchQuery,
            page: assessmentsPage,
            pageSize: assessmentsPageSize,
            courseIds: selectedCourses,
            moduleIds: selectedModules,
            sortBy,
          },
        },
      },
      {
        query: GetGradingCenterDashboardDataDocument,
        variables: {
          facultyId: meData?.me?.id || "",
        },
      },
      {
        query: GetAssessmentSubmissionsDetailsDocument,
        variables: {
          assessmentSubmissionId: selectedGradingData?.id || "",
        },
      },
      {
        query: GetAssessmentSubmissionAttemptHistoryDocument,
        variables: {
          assessmentSubmissionId: selectedGradingData?.id || "",
        },
      },
    ],
  });
  const [updateStudentAssignmentGrade] = useUpdateStudentAssignmentGradeMutation({
    refetchQueries: [
      {
        query: GetUserSubmissionsByFacultyDocument,
        variables: {
          input: {
            facultyId: meData?.me?.id || "",
            query: searchQuery,
            page: submissionsPage,
            pageSize: submissionsPageSize,
            courseIds: selectedCourses,
            moduleIds: selectedModules,
            sortBy,
          },
        },
      },
      {
        query: GetGradingCenterDashboardDataDocument,
        variables: {
          facultyId: meData?.me?.id || "",
        },
      },
    ],
  });

  const { data: courseData, loading: courseLoading } = useGetCoursesByFacultyQuery({
    variables: {
      userId: meData?.me?.id,
    },
    skip: !meData?.me?.id,
  });

  const { data: gradingCenterDashboardData, loading: gradingCenterDashboardLoading } =
    useGetGradingCenterDashboardDataQuery({
      variables: {
        facultyId: meData?.me?.id,
      },
      skip: !meData?.me?.id,
    });
  const gradingData = gradingCenterDashboardData?.getGradingCenterDashboardData?.data;

  const { data: assessmentSubmissionDetails, loading: assessmentSubmissionDetailsLoading } =
    useGetAssessmentSubmissionsDetailsQuery({
      variables: {
        assessmentSubmissionId: selectedGradingData?.id || "",
      },
      skip: !selectedGradingData?.id || selectedGradingData?.type !== "Assessment",
    });

  const { data: assessmentData, refetch: refetchAssessmentData } = useGetAssessmentSubmissionsByFacultyQuery({
    variables: {
      input: {
        facultyId: meData?.me?.id || "",
        query: searchQuery,
        page: assessmentsPage,
        pageSize: assessmentsPageSize,
        courseIds: selectedCourses,
        moduleIds: selectedModules,
        sortBy,
      },
    },
    skip: !meData?.me?.id,
  });

  const { data: userSubmissionsData, refetch: refetchUserSubmissionsData } = useGetUserSubmissionsByFacultyQuery({
    variables: {
      input: {
        facultyId: meData?.me?.id || "",
        query: searchQuery,
        page: submissionsPage,
        pageSize: submissionsPageSize,
        courseIds: selectedCourses,
        moduleIds: selectedModules,
        sortBy,
      },
    },
    skip: !meData?.me?.id,
  });

  const filters = useMemo(() => {
    return {
      courses: courseData?.getCoursesByFaculty?.data?.map((course) => ({
        key: course.id,
        value: course.name,
      })),
      modules: courseData?.getCoursesByFaculty?.data
        ?.map((course) =>
          course.modules.map((module) => ({
            key: module.id,
            value: module.title,
          }))
        )
        .flat(),
    };
  }, [courseData]);

  useEffect(() => {
    setSelectedCourses(filters.courses?.map((course) => course.key));
    setSelectedModules(filters.modules?.map((module) => module.key));
  }, [courseData]);

  useEffect(() => {
    refetchAssessmentData();
    refetchUserSubmissionsData();
  }, [refetchAssessmentData, refetchUserSubmissionsData, meData]);

  const assessmentSubmissions = useMemo(
    () =>
      assessmentData?.getAssessmentSubmissionsByFaculty?.data?.assessmentSubmissions.map((assessment) => ({
        userName: assessment.user.name,
        userId: assessment.user.id,
        courseName: assessment.assessment.moduleHasAssessment.courseHasModules[0].name,
        moduleName: assessment.assessment.moduleHasAssessment.title,
        assessmentId: assessment.assessment.id,
        ...assessment,
        type: "Assessment",
      })),
    [assessmentData]
  );

  const submissions = useMemo(
    () =>
      userSubmissionsData?.getUserSubmissionsByFaculty?.data?.submissions.map((submission) => ({
        ...submission,
        type: "Assignment",
      })),
    [userSubmissionsData]
  );

  const handleViewResponse = (item: GradingData) => {
    setSelectedGradingData(item);
    // Make sure we're properly checking the type
    if (item.type === "Assessment") {
      setModalState(ModalState.VIEW_ASSESSMENT_RESPONSE);
    } else {
      setModalState(ModalState.VIEW_ASSIGNMENT_RESPONSE);
    }
  };

  const handleSaveGrade = async (gradingData: GradingData, newScore: number, feedback?: string) => {
    setUpdateError(null);
    setIsSubmitting(true);

    if (gradingData.type === "Assessment") {
      try {
        await updateAssessmentGrade({
          variables: {
            assessmentSubmissionId: gradingData.id,
            grade: newScore,
          },
        });
      } catch (error) {
        if (error?.message?.includes("Evaluation is currently pending")) {
          setUpdateError(error.message);
        } else {
          setUpdateError(error.message);
        }
      }
    } else {
      await updateStudentAssignmentGrade({
        variables: {
          submissionId: gradingData.id,
          grade: newScore,
          feedback,
        },
      });
    }
    setModalState(ModalState.CLOSED);
    setIsSubmitting(false);
  };

  const handleCloseModal = () => {
    setModalState(ModalState.CLOSED);
    setSelectedGradingData(null);
  };

  useEffect(() => {
    setAssessmentsPage(1);
    setSubmissionsPage(1);
  }, [searchQuery, sortBy]);

  useEffect(() => {
    refetchAssessmentData();
    refetchUserSubmissionsData();
  }, [searchQuery, assessmentsPage, submissionsPage, assessmentsPageSize, submissionsPageSize, sortBy]);

  return (
    <DefaultLayout pageHasChat={false}>
      <MotiView
        from={{ opacity: 0, translateY: 20 }}
        animate={{ opacity: 1, translateY: 0 }}
        delay={10}
        // @ts-expect-error - MotiView transition props have complex typing that's incompatible with simple objects
        transition={{ type: "timing", duration: 500 }}>
        <CustomText size="m" textType="display" text="Grading Center" weight="ultraBold" />
      </MotiView>
      <CustomSpacing type="vertical" size="xl" />
      <View style={styles.contentContainer}>
        <DashboardDataContainer
          newSubmissionsToday={gradingData?.newSubmissionsToday}
          assignmentsAndAssessmentsToGrade={gradingData?.assignmentsAndAssessmentsToGrade}
          isLoading={gradingCenterDashboardLoading || gradingData === undefined}
        />
        <View style={styles.filterContainer}>
          <View style={styles.controlsContainer}>
            <SortingDropdown
              sortBy={sortBy}
              setSortBy={setSortBy}
              sortDropdownOpen={sortDropdownOpen}
              setSortDropdownOpen={setSortDropdownOpen}
              sortItems={SORT_ITEMS}
              label="Sort by"
            />
            <CustomSearchInput searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
          </View>
          <View style={styles.dropdownFilterContainer}>
            <CustomMultiSelect
              data={filters.courses}
              selectedItems={selectedCourses}
              setSelectedItems={setSelectedCourses}
              label="Course"
            />
            <CustomMultiSelect
              data={filters.modules}
              selectedItems={selectedModules}
              setSelectedItems={setSelectedModules}
              label="Module"
            />
          </View>
        </View>
        <View style={styles.tableContainer}>
          <CustomText text="Assessment Submissions" style={styles.tableTitle} weight="bold" />
          <GradingTableContainer
            gradingData={assessmentSubmissions ?? []}
            page={assessmentsPage}
            pageSize={assessmentsPageSize}
            setPage={setAssessmentsPage}
            setPageSize={setAssessmentsPageSize}
            totalCount={assessmentData?.getAssessmentSubmissionsByFaculty?.totalCount}
            handleRowPress={handleViewResponse}
          />
        </View>
        <CustomSpacing type="vertical" size="xl" />
        <View style={styles.tableContainer}>
          <CustomText text="Assignment Submissions" style={styles.tableTitle} weight="bold" />
          <GradingTableContainer
            gradingData={submissions ?? []}
            page={submissionsPage}
            pageSize={submissionsPageSize}
            setPage={setSubmissionsPage}
            setPageSize={setSubmissionsPageSize}
            totalCount={userSubmissionsData?.getUserSubmissionsByFaculty?.data?.totalCount}
            handleRowPress={handleViewResponse}
          />
        </View>
      </View>
      {modalState === ModalState.VIEW_ASSESSMENT_RESPONSE && (
        <AssessmentGradingModalContainer
          isVisible={true}
          onClose={handleCloseModal}
          isLoading={assessmentSubmissionDetailsLoading}
          gradingData={{
            ...(assessmentSubmissionDetails?.getAssessmentSubmissionDetails?.data as unknown as GradingData),
            type: "Assessment",
          }}
          onSave={handleSaveGrade}
          isSubmitting={isSubmitting}
          updateError={updateError}
          canEditScore={true}
        />
      )}

      {modalState === ModalState.VIEW_ASSIGNMENT_RESPONSE && (
        <AssignmentSubmissionGradingModal
          isVisible={true}
          onClose={handleCloseModal}
          gradingData={selectedGradingData}
          onSave={handleSaveGrade}
          isSubmitting={isSubmitting}
        />
      )}
    </DefaultLayout>
  );
};

const styles = StyleSheet.create({
  contentContainer: {
    position: "relative",
    zIndex: 1,
  },
  filterContainer: {
    backgroundColor: "#f9fafb",
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 16,
    zIndex: 50,
  },
  dropdownFilterContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    position: "relative",
    zIndex: 50,
  },
  tableContainer: {
    marginTop: 12,
    paddingHorizontal: 12,
  },
  tableTitle: {
    fontSize: 16,
    color: "#344054",
  },
  controlsContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
    zIndex: 3000,
    elevation: 3000,
  },
});

export default GradingCenterView;
